/**
 * String Field Component
 *
 * Renders a text input for string properties with expression mode support.
 * Users can toggle between fixed value and expression mode.
 * Expression mode provides:
 * - Live preview of evaluated expressions
 * - Insert field button to reference upstream node outputs
 * - Syntax highlighting for {{ }} expressions
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Code2,
  Type,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Wand2,
  Loader2,
} from 'lucide-react';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { optimizePrompt } from '@/services/promptOptimizerApi';
import { PromptOptimizeDialog } from '../PromptOptimizeDialog';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { DroppableInput } from '../../node-editor/DroppableInput';
import { DroppableTextarea } from '../../node-editor/DroppableTextarea';
import { CodeMirrorEditor, EditorLanguage } from '../../node-editor/CodeMirrorEditor';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface StringFieldProps {
  property: InferredPropertySchema;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  error?: string;
  allParameters?: Record<string, any>;
  inputData?: InputData[];
  onOpenAIHelp?: () => void; // Callback to open AI Help tab (for Code nodes)
  nodeType?: string; // Node type for prompt optimization context
}

// Helper to detect if value contains expressions
function containsExpression(value: string): boolean {
  return /\{\{.*?\}\}/.test(value || '');
}

// Helper to extract fields from an object recursively
function extractFields(obj: any, prefix = ''): { path: string; value: any; type: string }[] {
  const fields: { path: string; value: any; type: string }[] = [];

  if (!obj || typeof obj !== 'object') return fields;

  // Handle arrays
  if (Array.isArray(obj)) {
    if (obj.length > 0 && typeof obj[0] === 'object') {
      const arrayPath = prefix ? `${prefix}[0]` : '[0]';
      fields.push(...extractFields(obj[0], arrayPath));
    }
    return fields;
  }

  Object.keys(obj).forEach((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    const type = Array.isArray(value)
      ? 'array'
      : value === null
        ? 'null'
        : typeof value;

    fields.push({ path, value, type });

    // Recursively extract nested fields (limit depth to 5)
    if (typeof value === 'object' && value !== null && path.split('.').length < 5) {
      fields.push(...extractFields(value, path));
    }
  });

  return fields;
}

/**
 * Parse a property path into parts, properly handling bracket notation with quoted strings.
 * Examples:
 *   "data.name" -> ["data", "name"]
 *   "childOutputs["AI Agent"]" -> ["childOutputs", "AI Agent"]
 *   "items[0].name" -> ["items", "0", "name"]
 *   "data["key with spaces"].nested" -> ["data", "key with spaces", "nested"]
 */
function parsePathParts(path: string): string[] {
  if (!path) return [];

  const parts: string[] = [];
  let i = 0;

  while (i < path.length) {
    // Skip dots
    if (path[i] === '.') {
      i++;
      continue;
    }

    // Handle bracket notation
    if (path[i] === '[') {
      const closeBracket = path.indexOf(']', i);
      if (closeBracket === -1) {
        // Malformed, add rest as is
        parts.push(path.substring(i));
        break;
      }

      let content = path.substring(i + 1, closeBracket);

      // Strip quotes if present (both single and double)
      if ((content.startsWith('"') && content.endsWith('"')) ||
          (content.startsWith("'") && content.endsWith("'"))) {
        content = content.slice(1, -1);
      }

      parts.push(content);
      i = closeBracket + 1;
      continue;
    }

    // Regular property name - read until dot or bracket
    const start = i;
    while (i < path.length && path[i] !== '.' && path[i] !== '[') {
      i++;
    }

    const propName = path.substring(start, i);
    if (propName) {
      parts.push(propName);
    }
  }

  return parts;
}

// Simple expression evaluator for preview (client-side only)
function evaluateExpression(expr: string, inputData: InputData[]): { value: any; error: string | null } {
  try {
    const matches = expr.match(/\{\{(.*?)\}\}/g);
    if (!matches) return { value: expr, error: null };

    let result = expr;

    for (const match of matches) {
      const expression = match.slice(2, -2).trim();

      // Handle $node["NodeName"].output.field
      const nodeMatch = expression.match(/^\$node\["([^"]+)"\]\.output\.?(.*)$/);
      if (nodeMatch) {
        const [, nodeName, fieldPath] = nodeMatch;

        // Find all nodes matching the name
        const matchingNodes = inputData.filter(d => d.nodeName === nodeName);

        if (matchingNodes.length === 0) {
          return { value: null, error: `Node "${nodeName}" not found in upstream data` };
        }

        // Helper to unwrap unified format and get the first field
        const getUnwrappedOutput = (nodeData: InputData) => {
          let output = nodeData.output;
          if (output && typeof output === 'object' && 'output' in output) {
            output = output.output;
          }
          return output;
        };

        // Helper to check if a field path exists in output
        const hasFieldPath = (output: any, path: string): boolean => {
          if (!output || !path) return true; // No path means we just want the output
          const parts = parsePathParts(path);
          let current = output;
          for (const part of parts) {
            if (current === undefined || current === null) return false;
            // Handle ".item" virtual path for iterating nodes
            if (part === 'item' && Array.isArray(current)) {
              current = current[0];
            } else {
              current = current[part];
            }
          }
          return current !== undefined;
        };

        // Smart node selection:
        // 1. If there's only one match, use it
        // 2. If there are multiple, prefer the one that has the requested field
        // 3. If multiple have the field (or none do), prefer the closest one (smallest nodeDistance)
        let nodeData: InputData;
        if (matchingNodes.length === 1) {
          nodeData = matchingNodes[0];
        } else {
          // Sort by nodeDistance (closest first)
          const sortedNodes = [...matchingNodes].sort((a, b) => a.nodeDistance - b.nodeDistance);

          // Find a node that actually has the requested field
          const nodeWithField = sortedNodes.find(n => {
            const output = getUnwrappedOutput(n);
            return hasFieldPath(output, fieldPath);
          });

          // Use the node with the field, or fall back to the closest one
          nodeData = nodeWithField || sortedNodes[0];
        }

        let value = getUnwrappedOutput(nodeData);

        if (fieldPath) {
          const parts = parsePathParts(fieldPath);
          for (const part of parts) {
            if (value === undefined || value === null) {
              return { value: null, error: `Field "${fieldPath}" not found` };
            }
            // Handle ".item" virtual path segment for iterating nodes (AI Batch, Loop)
            // When "item" is in the path and the current value is an array,
            // use the first element for preview purposes
            if (part === 'item' && Array.isArray(value)) {
              value = value[0];
            } else {
              value = value[part];
            }
          }
        }

        if (expr === match) {
          return { value, error: null };
        }
        result = result.replace(match, typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
      }
      // Handle $json.field
      else if (expression.startsWith('$json.')) {
        const fieldPath = expression.substring(6);
        const firstNode = inputData.sort((a, b) => a.nodeDistance - b.nodeDistance)[0];
        if (!firstNode) {
          return { value: null, error: 'No upstream node data available' };
        }

        let value = firstNode.output;
        // Unwrap unified format if needed
        if (value && typeof value === 'object' && 'output' in value) {
          value = value.output;
        }
        const parts = parsePathParts(fieldPath);
        for (const part of parts) {
          if (value === undefined || value === null) break;
          value = value[part];
        }

        if (expr === match) {
          return { value, error: null };
        }
        result = result.replace(match, typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
      }
    }

    return { value: result, error: null };
  } catch (err) {
    return { value: null, error: err instanceof Error ? err.message : 'Invalid expression' };
  }
}

export function StringField({ property, value, onChange, onBlur, error, allParameters, inputData = [], onOpenAIHelp, nodeType }: StringFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpressionMode, setIsExpressionMode] = useState(() => containsExpression(value));
  const [fieldBrowserOpen, setFieldBrowserOpen] = useState(false);

  // Prompt optimization state
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);

  // Auth hooks for API calls
  const { getAccessToken } = useIdentity();
  const { subscriptionId } = useSubscription();

  // Track cursor position for inserting at cursor location
  const cursorPositionRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Check if this is an optimizable prompt field
  const isOptimizablePrompt = ['systemPrompt', 'userPrompt', 'userPromptTemplate'].includes(property.key);
  const promptType = property.key === 'systemPrompt' ? 'system' : 'user';
  const effectiveNodeType = nodeType === 'ai-agent' || nodeType === 'ai-batch' ? nodeType : 'ai-agent';

  // Handle prompt optimization
  const handleOptimize = async () => {
    if (!value?.trim() || isOptimizing) return;

    setIsOptimizing(true);
    setShowOptimizeDialog(true);
    setOptimizedPrompt('');

    try {
      const result = await optimizePrompt(
        {
          prompt: value,
          promptType: promptType as 'system' | 'user',
          nodeType: effectiveNodeType as 'ai-agent' | 'ai-batch',
        },
        {
          getAccessToken,
          subscriptionId: subscriptionId || undefined,
        }
      );

      if (result.success && result.optimizedPrompt) {
        setOptimizedPrompt(result.optimizedPrompt);
      } else {
        console.error('Prompt optimization failed:', result.error);
        setShowOptimizeDialog(false);
      }
    } catch (err) {
      console.error('Prompt optimization error:', err);
      setShowOptimizeDialog(false);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Apply optimized prompt
  const handleApplyOptimizedPrompt = () => {
    if (optimizedPrompt) {
      onChange(optimizedPrompt);
    }
  };

  // Auto-detect expression mode when value changes
  useEffect(() => {
    if (containsExpression(value) && !isExpressionMode) {
      setIsExpressionMode(true);
    }
  }, [value, isExpressionMode]);

  // Check if this should be rendered as a code editor
  const isCodeEditor = property.typeOptions?.editor === 'code';

  // Get editor language
  const editorLanguageFromProperty = property.typeOptions?.editorLanguageFromProperty;
  const editorLanguage = editorLanguageFromProperty && allParameters
    ? (allParameters[editorLanguageFromProperty] as string) || 'javascript'
    : property.typeOptions?.editorLanguage || 'javascript';


  // Check if this should be rendered as a textarea
  const rows = property.typeOptions?.rows;
  const isTextarea = rows && rows > 1;

  // Evaluate expression for preview
  const preview = useMemo(() => {
    if (!isExpressionMode || !containsExpression(value)) {
      return { value: null, error: null };
    }
    return evaluateExpression(value, inputData);
  }, [value, inputData, isExpressionMode]);

  // Check if this is arrayItem insert mode (for AI Batch template fields)
  const isArrayItemMode = property.typeOptions?.insertFieldMode === 'arrayItem';
  const insertFieldSource = property.typeOptions?.insertFieldSource; // e.g., 'inputData'

  // Extract fields from array items (for AI Batch node)
  // Uses insertFieldSource to look up the expression, then evaluates it to get the array
  // Generates expressions like: $node["Data Engine API"].output.data.item.fieldName
  const arrayItemFields = useMemo(() => {
    if (!isArrayItemMode) return [];

    const fields: { path: string; fullPath: string; value: any; type: string }[] = [];

    // Get the array by evaluating the source field's expression
    let arrayData: any[] | null = null;
    let baseExpressionPath: string | null = null;

    if (insertFieldSource && allParameters) {
      // Get the expression from the source field (e.g., allParameters.inputData)
      const sourceExpression = allParameters[insertFieldSource];

      if (sourceExpression && typeof sourceExpression === 'string' && containsExpression(sourceExpression)) {
        // Parse the expression to extract the base path
        // e.g., "{{ $node["Data Engine API"].output.data }}" -> "$node["Data Engine API"].output.data"
        const match = sourceExpression.match(/\{\{\s*(.+?)\s*\}\}/);
        if (match) {
          baseExpressionPath = match[1].trim();
        }

        // Evaluate the expression to get the actual array
        const result = evaluateExpression(sourceExpression, inputData);
        if (!result.error && Array.isArray(result.value)) {
          arrayData = result.value;
        }
      } else if (Array.isArray(sourceExpression)) {
        // Direct array value (not an expression)
        arrayData = sourceExpression;
      }
    }

    // If we found an array, get fields from first item
    if (arrayData && arrayData.length > 0) {
      const firstItem = arrayData[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        // Generate the item path - use node reference style if we have a base path
        // e.g., "$node["Data Engine API"].output.data.item" or fallback to "$loop.item"
        const itemPath = baseExpressionPath
          ? `${baseExpressionPath}.item`
          : '$loop.item';

        // Add the full item option first
        fields.push({
          path: 'item',
          fullPath: itemPath,
          value: firstItem,
          type: 'object',
        });

        // Extract nested fields
        const nestedFields = extractFields(firstItem);
        for (const field of nestedFields) {
          fields.push({
            path: `item.${field.path}`,
            fullPath: `${itemPath}.${field.path}`,
            value: field.value,
            type: field.type,
          });
        }
      }
    }

    return fields;
  }, [inputData, isArrayItemMode, insertFieldSource, allParameters]);

  // Extract available fields from upstream nodes
  const availableFields = useMemo(() => {
    const fields: { nodeName: string; path: string; fullPath: string; value: any; type: string }[] = [];
    const sortedInput = [...inputData].sort((a, b) => a.nodeDistance - b.nodeDistance);

    for (const input of sortedInput) {
      let outputData = input.output;
      if (outputData && typeof outputData === 'object' && 'output' in outputData) {
        outputData = outputData.output;
      }
      if (!outputData) continue;

      const nodeFields = extractFields(outputData);
      for (const field of nodeFields) {
        fields.push({
          nodeName: input.nodeName,
          path: field.path,
          fullPath: `$node["${input.nodeName}"].output.${field.path}`,
          value: field.value,
          type: field.type,
        });
      }
    }

    return fields;
  }, [inputData]);

  // Group fields by node
  const fieldsByNode = useMemo(() => {
    const grouped: Record<string, typeof availableFields> = {};
    for (const field of availableFields) {
      if (!grouped[field.nodeName]) {
        grouped[field.nodeName] = [];
      }
      grouped[field.nodeName].push(field);
    }
    return grouped;
  }, [availableFields]);

  // Capture cursor position before opening field browser
  const saveCursorPosition = () => {
    if (inputRef.current) {
      cursorPositionRef.current = inputRef.current.selectionStart ?? null;
    }
  };

  // Insert expression at cursor position
  const insertExpression = (expression: string) => {
    const expressionText = `{{${expression}}}`;
    const currentValue = value || '';
    const cursorPos = cursorPositionRef.current;

    let newValue: string;
    if (cursorPos !== null && cursorPos >= 0 && cursorPos <= currentValue.length) {
      // Insert at cursor position
      newValue = currentValue.slice(0, cursorPos) + expressionText + currentValue.slice(cursorPos);
    } else {
      // Fallback: append to end
      newValue = currentValue + expressionText;
    }

    onChange(newValue);
    setFieldBrowserOpen(false);

    // Reset cursor position after insert
    cursorPositionRef.current = null;
  };

  // Toggle expression mode
  const toggleExpressionMode = () => {
    setIsExpressionMode(!isExpressionMode);
  };

  // Get type badge color
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'string': return 'bg-blue-500/10 text-blue-600';
      case 'number': return 'bg-green-500/10 text-green-600';
      case 'boolean': return 'bg-purple-500/10 text-purple-600';
      case 'array': return 'bg-pink-500/10 text-pink-600';
      case 'object': return 'bg-orange-500/10 text-orange-600';
      default: return 'bg-gray-500/10 text-gray-600';
    }
  };

  // Render mode toggle
  const renderModeToggle = () => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant={isExpressionMode ? 'default' : 'ghost'}
            size="sm"
            className={cn(
              'h-6 w-6 p-0',
              isExpressionMode && 'bg-blue-500 hover:bg-blue-600'
            )}
            onClick={toggleExpressionMode}
          >
            {isExpressionMode ? (
              <Code2 className="h-3.5 w-3.5" />
            ) : (
              <Type className="h-3.5 w-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">
            {isExpressionMode ? 'Switch to fixed value' : 'Switch to expression mode'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Render optimize button for prompt fields
  const renderOptimizeButton = () => {
    if (!isOptimizablePrompt || !value?.trim()) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              disabled={isOptimizing}
              onClick={handleOptimize}
            >
              {isOptimizing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">Optimise prompt with AI</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Render field browser - handles both standard mode and arrayItem mode
  const renderFieldBrowser = () => {
    // Determine which fields to show based on mode
    const hasArrayItemFields = isArrayItemMode && arrayItemFields.length > 0;
    const hasStandardFields = Object.keys(fieldsByNode).length > 0;
    const isDisabled = isArrayItemMode ? !hasArrayItemFields : !hasStandardFields;

    return (
      <Popover open={fieldBrowserOpen} onOpenChange={(open) => {
        if (open) {
          saveCursorPosition();
        }
        setFieldBrowserOpen(open);
      }}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs gap-1"
            disabled={isDisabled}
            onMouseDown={saveCursorPosition}
          >
            <Sparkles className="h-3 w-3" />
            Insert Field
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-2 border-b">
            <p className="text-xs font-medium">
              {isArrayItemMode ? 'Array Item Fields' : 'Available Fields'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isArrayItemMode
                ? 'Click to insert {{...item.field}}'
                : 'Click to insert as expression'}
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto p-1">
            {isArrayItemMode ? (
              // Array Item Mode - show fields from array items
              arrayItemFields.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <p>No array data available</p>
                  <p className="text-xs mt-1">Run previous nodes to see available fields</p>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {arrayItemFields.map((field, idx) => (
                    <button
                      key={`${field.fullPath}-${idx}`}
                      className="w-full text-left px-2 py-1.5 hover:bg-muted rounded text-xs flex items-center gap-2 group"
                      onClick={() => insertExpression(field.fullPath)}
                    >
                      <code className="font-mono text-xs flex-1 truncate">
                        {field.path}
                      </code>
                      <Badge className={cn('text-[10px] px-1', getTypeBadgeColor(field.type))}>
                        {field.type}
                      </Badge>
                    </button>
                  ))}
                </div>
              )
            ) : (
              // Standard Mode - show upstream node fields
              Object.keys(fieldsByNode).length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <p>No upstream data available</p>
                  <p className="text-xs mt-1">Run previous nodes to see available fields</p>
                </div>
              ) : (
                Object.entries(fieldsByNode).map(([nodeName, fields]) => (
                  <Collapsible key={nodeName} defaultOpen={Object.keys(fieldsByNode).length === 1}>
                    <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 hover:bg-muted rounded text-sm font-medium">
                      <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" />
                      {nodeName}
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {fields.length}
                      </Badge>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="ml-4 space-y-0.5">
                        {fields.slice(0, 20).map((field, idx) => (
                          <button
                            key={`${field.fullPath}-${idx}`}
                            className="w-full text-left px-2 py-1.5 hover:bg-muted rounded text-xs flex items-center gap-2 group"
                            onClick={() => insertExpression(field.fullPath)}
                          >
                            <code className="font-mono text-xs flex-1 truncate">
                              {field.path}
                            </code>
                            <Badge className={cn('text-[10px] px-1', getTypeBadgeColor(field.type))}>
                              {field.type}
                            </Badge>
                          </button>
                        ))}
                        {fields.length > 20 && (
                          <p className="text-xs text-muted-foreground px-2 py-1">
                            +{fields.length - 20} more fields
                          </p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Render preview
  const renderPreview = () => {
    if (!isExpressionMode || !containsExpression(value)) return null;

    return (
      <div className="mt-2 p-2 rounded-md border bg-muted/30">
        <div className="flex items-start gap-2">
          {preview.error ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-destructive">Preview Error</div>
                <div className="text-xs text-muted-foreground mt-0.5">{preview.error}</div>
              </div>
            </>
          ) : preview.value !== null ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium">Preview</div>
                <div className="text-xs text-muted-foreground mt-0.5 font-mono break-all">
                  {typeof preview.value === 'object'
                    ? JSON.stringify(preview.value, null, 2).substring(0, 200)
                    : String(preview.value).substring(0, 200)}
                  {(typeof preview.value === 'string' && preview.value.length > 200) && '...'}
                </div>
              </div>
            </>
          ) : (
            <>
              <Code2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-xs text-muted-foreground">
                Expression detected. Run upstream nodes to see preview.
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 min-w-0 w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.helpText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{property.helpText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isExpressionMode && containsExpression(value) && (
            <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600">
              <Code2 className="h-3 w-3 mr-1" />
              Expression
            </Badge>
          )}
        </div>

        {!isCodeEditor && !property.readOnly && (
          <div className="flex items-center gap-1">
            {renderOptimizeButton()}
            {(isExpressionMode || isArrayItemMode) && renderFieldBrowser()}
            {renderModeToggle()}
          </div>
        )}
      </div>


      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}


      {isCodeEditor ? (
        <CodeMirrorEditor
          value={value || ''}
          onChange={onChange}
          language={editorLanguage as EditorLanguage}
          height={isExpanded ? '500px' : '300px'}
          placeholder={property.placeholder}
          showAIHelp={!!onOpenAIHelp}
          onOpenAIHelp={onOpenAIHelp}
          inputData={inputData?.map(d => ({
            nodeId: d.nodeId,
            nodeName: d.nodeName,
            nodeDistance: d.nodeDistance,
            data: d.output,
            sourceHandle: d.sourceHandle,
          }))}
          executionTier={(allParameters?.executionTier as 'sandboxed' | 'extended') || 'sandboxed'}
        />
      ) : isTextarea ? (
        <div className="relative">
          <DroppableTextarea
            ref={(el) => { inputRef.current = el; }}
            id={property.key}
            value={value || ''}
            onValueChange={onChange}
            placeholder={isExpressionMode
              ? 'Enter expression, e.g., {{ $node["NodeName"].output.field }}'
              : property.placeholder
            }
            rows={isExpanded ? 20 : rows}
            disabled={property.readOnly}
            className={cn(
              error ? 'border-destructive' : '',
              isExpanded ? 'min-h-[500px]' : '',
              isExpressionMode && containsExpression(value) && 'border-blue-500/50 bg-blue-500/5 font-mono text-sm',
              property.readOnly && 'bg-muted cursor-not-allowed opacity-70'
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ) : (
        <DroppableInput
          ref={(el) => { inputRef.current = el; }}
          id={property.key}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur ? (e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value || '') : undefined}
          placeholder={isExpressionMode
            ? 'e.g., {{ $node["NodeName"].output.field }}'
            : property.placeholder
          }
          disabled={property.readOnly}
          className={cn(
            error ? 'border-destructive' : '',
            isExpressionMode && containsExpression(value) && 'border-blue-500/50 bg-blue-500/5 font-mono text-sm',
            property.readOnly && 'bg-muted cursor-not-allowed opacity-70'
          )}
        />
      )}

      {renderPreview()}

      {isExpressionMode && !containsExpression(value) && (
        <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
          <p className="font-medium mb-1">Expression Syntax:</p>
          <ul className="space-y-0.5 ml-2">
            <li><code className="bg-muted px-1 rounded">{'{{ $node["NodeName"].output.field }}'}</code> - Reference upstream node</li>
            <li><code className="bg-muted px-1 rounded">{'{{ $json.fieldName }}'}</code> - Reference input data</li>
          </ul>
        </div>
      )}


      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Prompt Optimization Dialog */}
      {isOptimizablePrompt && (
        <PromptOptimizeDialog
          open={showOptimizeDialog}
          onOpenChange={setShowOptimizeDialog}
          originalPrompt={value || ''}
          optimizedPrompt={optimizedPrompt}
          onApply={handleApplyOptimizedPrompt}
          isLoading={isOptimizing}
          promptType={promptType as 'system' | 'user'}
        />
      )}
    </div>
  );
}
